import { useCopyToClipboard } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useState } from 'react';
import { DialogTrigger, TextField } from 'react-aria-components';

import {
  BasicTooltip,
  Button,
  Flex,
  Heading,
  Input,
  Label,
  Separator,
  Text,
  TextArea,
  useComponentStack,
} from '@/components/core';
import { CheckmarkIcon, PromptIcon } from '@/components/icons';
import { DeleteModal, findField, FormattersSection, NewFieldHelpProps, ValidatorsSection } from '@/components/ui';
import { useModelConfigStore } from '@/store';
import { toString } from '@/utils';

export type FieldDetailsProps = {
  fieldId: string;
  newFieldHelp?: NewFieldHelpProps;
};

const IFieldDetails = ({ fieldId, newFieldHelp }: FieldDetailsProps) => {
  const updateField = useModelConfigStore((state) => state.updateField);
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const deleteField = useModelConfigStore((state) => state.deleteField);
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [hasCopied, setHasCopied] = useState(false);
  const componentStack = useComponentStack();

  useEffect(() => {
    if (copiedText) {
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    }
  }, [copiedText]);

  const onDeleteField = useCallback(() => {
    deleteField(fieldId, field?.parentId);
    componentStack?.pop();
  }, [componentStack, deleteField, field?.parentId, fieldId]);

  const isTableField = field?.type === 'table';

  if (!field) {
    return null;
  }

  return (
    <Flex col className="p-md gap-md group/field-details h-full">
      <TextField
        className="flex flex-col gap-1"
        defaultValue={newFieldHelp ? newFieldHelp.defaultName : field.name}
        onChange={(name) => updateField(field.id, { name }, field.parentId)}
      >
        <div className="gap-xs flex items-center">
          <Label isRequired>Field name</Label>
          {!isTableField ? (
            <>
              <div className="grow" />
              <Label className="text-gray-400">ID:</Label>
              <Label className="cursor-pointer" onClick={() => copyToClipboard(fieldId)}>
                {fieldId}
              </Label>
              {hasCopied ? <CheckmarkIcon className="animate-fade opacity-0" /> : null}
            </>
          ) : null}
        </div>
        <Input placeholder={newFieldHelp?.namePlaceholder || 'My new field'} />
      </TextField>
      <div className="gap-sm flex flex-col rounded-lg border border-gray-200 bg-gray-50 p-2">
        <div className="gap-xs flex items-center">
          <PromptIcon className="text-primary-500" />
          <Label isRequired>Prompt</Label>
          <BasicTooltip>
            <Text slot="description">
              Describe what the model should extract or understand. Give clear and specific prompts.
            </Text>
          </BasicTooltip>
        </div>
        <TextField
          className="flex flex-col gap-1"
          defaultValue={toString(field.promptHint)}
          onChange={(promptHint) => updateField(field.id, { promptHint }, field.parentId)}
        >
          <TextArea placeholder={newFieldHelp?.promptHintPlaceholder} />
        </TextField>
        <Text className="self-end" size="xs">
          Read guide in our{' '}
          <span
            onClick={() => window.open('https://docs.cradl.ai/core-concepts/ai-model/prompting', '_blank')}
            className="cursor-pointer font-semibold underline"
          >
            docs
          </span>{' '}
        </Text>
      </div>

      {!isTableField ? (
        <>
          <Separator className="my-2" orientation="horizontal" />

          <Heading size="sm" className="!font-regular text-gray-500 uppercase">
            Post processing
          </Heading>

          <FormattersSection fieldId={fieldId} />
          <ValidatorsSection fieldId={fieldId} />
        </>
      ) : null}

      <div className="grow" />

      <Separator className="my-2" orientation="horizontal" />

      <DialogTrigger>
        <Button className="mx-4 w-fit" variant="danger">
          <Text>Delete field</Text>
        </Button>
        <DeleteModal
          heading="Delete field"
          description="Are you sure you want to delete this field? This action cannot be undone."
          onDelete={onDeleteField}
        />
      </DialogTrigger>
    </Flex>
  );
};

export const FieldDetails = memo(IFieldDetails) as typeof IFieldDetails;
